import axios, { AxiosResponse } from 'axios';
import { message } from 'antd';
import { BASE_URL } from 'constants/api';
import {
  SeasonContentListResponseInterface,
  SeasonContentAddRequestInterface,
  SeasonContentAddResponseInterface,
  SeasonContentRemoveRequestInterface,
  SeasonContentRemoveResponseInterface,
} from '../types/seasonContentTypes';

/**
 * Season Content Link API 서비스
 * 새로운 /api/v1/season/{id}/contents/ API를 위한 전용 서비스
 *
 * 처리 방식:
 * - Season에 연결된 content 조회, 추가, 교체, 제거 기능 제공
 * - clip/trailer 타입의 AdditionalContentOriginLink도 함께 관리
 */
class SeasonContentService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${BASE_URL}/api/v1/season`;
  }

  /**
   * Season에 연결된 모든 content 목록 조회
   * @param seasonId - Season ID
   * @param queryParams - 쿼리 파라미터 (선택사항)
   * @returns Season에 연결된 content 목록
   */
  public async getSeasonContents(
    seasonId: number,
    queryParams?: URLSearchParams
  ): Promise<SeasonContentListResponseInterface> {
    try {
      const url = queryParams
        ? `${this.baseUrl}/${seasonId}/contents/?${queryParams.toString()}`
        : `${this.baseUrl}/${seasonId}/contents/`;
      const response: AxiosResponse<SeasonContentListResponseInterface> = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Season content 목록 조회 실패:', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        message.error('시즌을 찾을 수 없습니다.');
      } else {
        message.error('Season content 목록을 가져오는 중 오류가 발생했습니다.');
      }
      throw error;
    }
  }

  /**
   * Season에 content 추가
   * @param seasonId - Season ID
   * @param contentIds - 추가할 content ID 배열
   * @returns 추가 결과
   */
  public async addContentsToSeason(
    seasonId: number,
    contentIds: number[]
  ): Promise<SeasonContentAddResponseInterface> {
    try {
      const requestData: SeasonContentAddRequestInterface = { content_ids: contentIds };
      const response: AxiosResponse<SeasonContentAddResponseInterface> = await axios.post(
        `${this.baseUrl}/${seasonId}/contents/`,
        requestData
      );

      message.success(response.data.message);
      return response.data;
    } catch (error) {
      console.error('Season에 content 추가 실패:', error);
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          message.error('시즌을 찾을 수 없습니다.');
        } else if (error.response?.status === 400) {
          message.error(error.response.data?.error || 'Content 추가 요청이 잘못되었습니다.');
        } else {
          message.error('Content 추가 중 오류가 발생했습니다.');
        }
      } else {
        message.error('Content 추가 중 알 수 없는 오류가 발생했습니다.');
      }
      throw error;
    }
  }

  /**
   * Season에서 특정 content 연결 해제
   * @param seasonId - Season ID
   * @param contentIds - 제거할 content ID 배열
   * @returns 제거 결과
   */
  public async removeContentsFromSeason(
    seasonId: number,
    contentIds: number[]
  ): Promise<SeasonContentRemoveResponseInterface> {
    try {
      const requestData: SeasonContentRemoveRequestInterface = { content_ids: contentIds };
      const response: AxiosResponse<SeasonContentRemoveResponseInterface> = await axios.delete(
        `${this.baseUrl}/${seasonId}/contents/`,
        { data: requestData }
      );

      message.success(response.data.message);
      return response.data;
    } catch (error) {
      console.error('Season에서 content 제거 실패:', error);
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          message.error('시즌을 찾을 수 없습니다.');
        } else if (error.response?.status === 400) {
          message.error(error.response.data?.error || 'Content 제거 요청이 잘못되었습니다.');
        } else {
          message.error('Content 제거 중 오류가 발생했습니다.');
        }
      } else {
        message.error('Content 제거 중 알 수 없는 오류가 발생했습니다.');
      }
      throw error;
    }
  }
}

export const seasonContentService = new SeasonContentService();
